import related from "~/data/related.json";

/**
 * A SurrealDB University lesson or blog post that covers the subject of a docs
 * page. Generated by `scripts/generate-related.mjs`; see that file for how a
 * pairing is chosen and how to remove a wrong one.
 */
export interface RelatedItem {
    kind: "lesson" | "post";
    title: string;
    /**
     * The heading of the part of the item that matched the page, when it is
     * not the item's opening. The URL then links to that part.
     */
    section?: string;
    /** Site-relative, such as `/learn/book/chapter-12#everything-in-surrealdb-is-a-transaction`. */
    url: string;
}

const RELATED = related as Record<string, RelatedItem[]>;

/** How each kind of item is named, on the page and in its markdown. */
export const RELATED_SOURCE_LABELS: Record<RelatedItem["kind"], string> = {
    lesson: "SurrealDB University",
    post: "Blog",
};

/** The heading of the box, and of the list in the markdown version of a page. */
export const RELATED_TITLE = "Learn more about this topic";

/** The website the lessons and posts live on, outside the docs. */
const SITE = "https://surrealdb.com";

/** Related items for a docs page, keyed by its path including `/docs`. */
export function getRelated(path: string): RelatedItem[] {
    return RELATED[path] ?? [];
}

/**
 * The absolute URL of a related item. A site-relative link would resolve
 * against the docs origin, which is only the website's origin in production,
 * so it would 404 in local development and on deployment previews.
 */
export function relatedHref(item: RelatedItem): string {
    return `${SITE}${item.url}`;
}

/**
 * Append the related items to the markdown served for a page's `.md` URL, so an
 * agent reading the markdown is offered the same lessons and posts as a reader
 * of the page.
 *
 * Applied at the response, like `withIndexPointer`, rather than inside
 * `composeRawMarkdown`, which `llms-full.txt` reuses for every page.
 *
 * @param path - The page path including `/docs`, as `getRelated` takes it.
 */
export function withRelated(markdown: string, path: string): string {
    const items = getRelated(path);

    if (items.length === 0) return markdown;

    const list = items
        .map((item) => {
            const section = item.section ? `, section "${item.section}"` : "";
            return `- [${item.title}](${relatedHref(item)}) (${RELATED_SOURCE_LABELS[item.kind]}${section})`;
        })
        .join("\n");

    return `${markdown.trimEnd()}\n\n## ${RELATED_TITLE}\n\n${list}\n`;
}
